import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
  API,
  CourseDraftResponse,
  CourseDraftSaveReason,
  CourseLiveState,
  CourseVersionKind,
  CourseVersionListItem,
  ProgramEditLock
} from '../../../../../../../../api';
import { ModuleDraft } from '../../../../../../../../components/admin3/newProgram/masterclass/types';
import { useAppSelector } from '../../../../../../../../redux/hooks';
import { isVersioningOffError } from '../../../../../../../../utils/courseVersioningSession';
import { mapTemplateToModules } from '../utils';
import { buildTemplateLevelsPayload } from '../designMappers';
import type { LessonTags } from '../../../../../aimd-v2/lessonTags';
import { formatDateTime } from '../../../../../../../../utils/formatDate';

/**
 * State for the draft → Publish update model (docs/API-course-versioning.md).
 *
 * The provider owns the program's draft: it loads it on entry, autosaves the
 * editor's modules (debounced, plus a flush when the tab is hidden or
 * closed), holds the program edit lock while the Designer is open, lists the
 * version history and serves the read-only preview / restore of an older
 * version. DesignBuilder reports its modules here and registers the two
 * handlers the provider needs to write back into the editor: a full
 * replacement (draft reload, restore) and a functional update (server ids
 * adopted after a save so new lessons keep their identity).
 */

export type SaveState = 'idle' | 'pending' | 'saving' | 'saved' | 'error';

export interface VersionEntry {
  id: string;
  at: Date;
  kind: CourseVersionKind;
  label: string;
  author: string;
}
export interface VersionPreviewEntry extends VersionEntry {
  snapshot: ModuleDraft[];
}

interface CourseVersioningValue {
  grpId: string;
  /** Draft fetched and mapped; the editor waits for it. */
  draftLoaded: boolean;
  draftError: string;
  /** Editor seed from the draft's levels; a new array on every (re)load. */
  draftModules: ModuleDraft[] | null;
  live: CourseLiveState | null;
  liveVersion: number;
  /** Draft content differs from live (server), or an edit is waiting to be saved. */
  hasChanges: boolean;
  saveState: SaveState;
  /** unix seconds of the last draft write (server clock), null when never written. */
  savedAt: number | null;
  /** The program's capabilities and outcomes, when its lessons carry tags (docs/PRD-capability-lesson-roleplays.md R8). */
  lessonTags: LessonTags | null;
  /** Another admin holds the edit lock: the editor is read-only. */
  lockHolder: ProgramEditLock | null;
  readOnly: boolean;
  /** A 409 EDIT_LOCK seen elsewhere (publish) switches the editor to read-only. */
  noteLockHolder: (lock: ProgramEditLock | null) => void;
  entries: VersionEntry[];
  entriesLoading: boolean;
  panelOpen: boolean;
  setPanelOpen: (open: boolean) => void;
  previewEntry: VersionPreviewEntry | null;
  previewLoadingId: string | null;
  openPreview: (id: string) => void;
  resume: () => void;
  restore: () => void;
  publishOpen: boolean;
  setPublishOpen: (open: boolean) => void;
  /** Flushes pending edits, then opens Publish update. */
  openPublish: () => Promise<void>;
  /** Reloads draft + history after a publish; the editor gets the reset draft. */
  afterPublish: (versionNumber: number) => Promise<void>;
  /** Lesson titles by template id (sourceId) from the current editor state. */
  lessonTitles: Map<string, string>;
  reportDraft: (modules: ModuleDraft[]) => void;
  flushDraft: () => Promise<boolean>;
  reloadDraft: () => Promise<void>;
  /** The draft's modules, fresh from the server (targeted lesson refresh after AI / section writes). */
  fetchDraftModules: () => Promise<ModuleDraft[] | null>;
  registerRestoreHandler: (handler: ((snapshot: ModuleDraft[]) => void) | null) => void;
  registerModulesUpdater: (updater: ((fn: (prev: ModuleDraft[]) => ModuleDraft[]) => void) | null) => void;
}

const CourseVersioningContext = createContext<CourseVersioningValue | null>(null);

/** Null outside the provider (configuration view, flag off, frozen org). */
export const useCourseVersioning = () => useContext(CourseVersioningContext);

const AUTOSAVE_DEBOUNCE_MS = 3_000;
const AUTOSAVE_RETRY_MS = 10_000;
const LOCK_HEARTBEAT_MS = 30_000;
const VERSIONS_PAGE = 50;

// The versioning migration seeded every existing course with a v1 snapshot
// whose createdBy is the literal 'migration' (no user), which the BE echoes
// back as the author name.
const isMigrationSeed = (v: Pick<CourseVersionListItem, 'trigger'>): boolean => v.trigger === 'migration';

export const versionLabel = (v: Pick<CourseVersionListItem, 'kind' | 'versionNumber' | 'trigger'>): string => {
  if (isMigrationSeed(v)) return 'Original version';
  switch (v.kind) {
    case 'published':
      return `Published v${v.versionNumber ?? ''}`.trim();
    case 'before_restore':
      return 'Before restore';
    case 'before_ai':
      return 'Before Elliott’s change';
    default:
      return 'Autosave';
  }
};

/** List item → drawer row. Shared with the AIMD wizard's generation versions (same item shape). */
export const toVersionEntry = (v: CourseVersionListItem): VersionEntry => ({
  id: v._id,
  at: new Date((Number(v.createdAt) || 0) * 1000),
  kind: v.kind,
  label: versionLabel(v),
  author: isMigrationSeed(v) ? '' : v.createdBy?.name || ''
});
const toEntry = toVersionEntry;

const draftToModules = (levels: any[]): ModuleDraft[] => mapTemplateToModules({ levels: levels || [] });
const payloadJson = (modules: ModuleDraft[]): string => JSON.stringify(buildTemplateLevelsPayload(modules));

/** True when a save payload carried modules / lessons / questions without a server id. */
function needsIdAdoption(modules: ModuleDraft[]): boolean {
  return modules.some(
    (m) =>
      !m.sourceId ||
      m.lessons.some(
        (l) => !l.sourceId || l.multipleChoiceQuestions.some((q) => !q._id) || l.feedbackQuestions.some((q) => !q._id)
      )
  );
}

/**
 * After a save, copy the ids the server minted onto the editor's items. The
 * saved payload and the refetched draft share the same order (the payload
 * numbers levels and tasks by index and the server sorts by them), so items
 * are matched by position between `sent` and `fresh`, then applied to the
 * current editor state by the local ids of `sent` — robust to edits, reorders
 * or deletions that happened while the request was in flight. Only missing
 * ids are filled; content is never touched.
 */
export function adoptServerIds(current: ModuleDraft[], sent: ModuleDraft[], fresh: ModuleDraft[]): ModuleDraft[] {
  type LessonIds = { sourceId?: string; mcqIds: (string | undefined)[]; feedbackIds: (string | undefined)[] };
  const lessonIds = new Map<string, LessonIds>();
  const moduleIds = new Map<string, string>();
  sent.forEach((m, mi) => {
    const fm = fresh[mi];
    if (!fm) return;
    if (m.sourceId && fm.sourceId !== m.sourceId) return; // order mismatch: leave this module alone
    if (!m.sourceId && fm.sourceId) moduleIds.set(m.id, fm.sourceId);
    m.lessons.forEach((l, li) => {
      const fl = fm.lessons[li];
      if (!fl) return;
      if (l.sourceId && fl.sourceId !== l.sourceId) return;
      const sameQuizShape = fl.multipleChoiceQuestions.length === l.multipleChoiceQuestions.length;
      const sameFeedbackShape = fl.feedbackQuestions.length === l.feedbackQuestions.length;
      lessonIds.set(l.id, {
        sourceId: !l.sourceId ? fl.sourceId : undefined,
        mcqIds: sameQuizShape ? fl.multipleChoiceQuestions.map((q) => q._id) : [],
        feedbackIds: sameFeedbackShape ? fl.feedbackQuestions.map((q) => q._id) : []
      });
    });
  });
  if (!moduleIds.size && !lessonIds.size) return current;

  let changed = false;
  const next = current.map((m) => {
    let mod = m;
    const moduleSourceId = moduleIds.get(m.id);
    if (!m.sourceId && moduleSourceId) {
      mod = { ...mod, sourceId: moduleSourceId };
      changed = true;
    }
    let lessonsChanged = false;
    const lessons = mod.lessons.map((l) => {
      const ids = lessonIds.get(l.id);
      if (!ids) return l;
      let lesson = l;
      if (!l.sourceId && ids.sourceId) {
        lesson = { ...lesson, sourceId: ids.sourceId };
        lessonsChanged = true;
      }
      if (ids.mcqIds.length === l.multipleChoiceQuestions.length && l.multipleChoiceQuestions.some((q) => !q._id)) {
        lesson = {
          ...lesson,
          multipleChoiceQuestions: l.multipleChoiceQuestions.map((q, i) =>
            !q._id && ids.mcqIds[i] ? { ...q, _id: ids.mcqIds[i] } : q
          )
        };
        lessonsChanged = true;
      }
      if (ids.feedbackIds.length === l.feedbackQuestions.length && l.feedbackQuestions.some((q) => !q._id)) {
        lesson = {
          ...lesson,
          feedbackQuestions: l.feedbackQuestions.map((q, i) =>
            !q._id && ids.feedbackIds[i] ? { ...q, _id: ids.feedbackIds[i] } : q
          )
        };
        lessonsChanged = true;
      }
      return lesson;
    });
    if (lessonsChanged) {
      mod = { ...mod, lessons };
      changed = true;
    }
    return mod;
  });
  return changed ? next : current;
}

const errorCode = (err: any): { status?: number; code?: string; data?: any } => ({
  status: err?.response?.status,
  code: err?.response?.data?.code,
  data: err?.response?.data
});

export function CourseVersioningProvider({
  enabled,
  grpId,
  children
}: {
  enabled: boolean;
  grpId: string;
  children: React.ReactNode;
}) {
  const myUserId: string = useAppSelector((s: any) => s.userProfile?.data?._id) || '';
  /* A lock only belongs to someone else when we can prove it: while the profile
     is still loading `myUserId` is '', and comparing against that made every
     lock — our own included — read as another admin's and put the Designer into
     read-only against its own holder. Unknown identity therefore means "not
     someone else", and the heartbeat below re-evaluates a moment later. */
  const isOtherHolder = useCallback(
    (lock: ProgramEditLock | null | undefined): boolean =>
      Boolean(lock && myUserId && String(lock.userId) !== String(myUserId)),
    [myUserId]
  );

  const [draftLoaded, setDraftLoaded] = useState(false);
  const [draftError, setDraftError] = useState('');
  const [draftModules, setDraftModules] = useState<ModuleDraft[] | null>(null);
  const [live, setLive] = useState<CourseLiveState | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [lessonTags, setLessonTags] = useState<LessonTags | null>(null);
  const [lockHolder, setLockHolder] = useState<ProgramEditLock | null>(null);
  const [entries, setEntries] = useState<VersionEntry[]>([]);
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [previewEntry, setPreviewEntry] = useState<VersionPreviewEntry | null>(null);
  const [previewLoadingId, setPreviewLoadingId] = useState<string | null>(null);
  const [publishOpen, setPublishOpen] = useState(false);
  const [editorModules, setEditorModules] = useState<ModuleDraft[] | null>(null);

  const baseVersionRef = useRef(1);
  const currentModulesRef = useRef<ModuleDraft[] | null>(null);
  const hydrationPendingRef = useRef(true);
  const lastSavedJsonRef = useRef<string | null>(null);
  const pendingRef = useRef<{ modules: ModuleDraft[]; payload: any[]; json: string } | null>(null);
  const savingRef = useRef(false);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lockHeldRef = useRef(false);
  const restoreHandlerRef = useRef<((snapshot: ModuleDraft[]) => void) | null>(null);
  const modulesUpdaterRef = useRef<((fn: (prev: ModuleDraft[]) => ModuleDraft[]) => void) | null>(null);
  const flushRef = useRef<(reason?: CourseDraftSaveReason) => Promise<boolean>>(async () => true);
  const unmountedRef = useRef(false);

  useEffect(
    () => () => {
      unmountedRef.current = true;
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    },
    []
  );

  // ── Draft ──────────────────────────────────────────────────────────────────

  const applyDraft = useCallback(
    (data: CourseDraftResponse, pushToEditor: boolean) => {
      baseVersionRef.current = data.draft.baseVersion;
      setLive(data.live);
      setDirty(data.draft.dirty);
      setSavedAt(data.draft.updatedAt || null);
      setLessonTags(data.lessonTags ?? null);
      if (isOtherHolder(data.lock)) setLockHolder(data.lock);
      const mapped = draftToModules(data.draft.levels);
      pendingRef.current = null;
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      hydrationPendingRef.current = true;
      lastSavedJsonRef.current = payloadJson(mapped);
      setDraftModules(mapped);
      setSaveState('idle');
      if (pushToEditor) restoreHandlerRef.current?.(mapped);
    },
    [isOtherHolder]
  );

  const fetchDraft = useCallback(async (): Promise<CourseDraftResponse | null> => {
    const res = await API.getCourseDraft(grpId);
    return res?.data?.data ?? null;
  }, [grpId]);

  const fetchDraftModules = useCallback(async (): Promise<ModuleDraft[] | null> => {
    try {
      const data = await fetchDraft();
      return data ? draftToModules(data.draft.levels) : null;
    } catch {
      return null;
    }
  }, [fetchDraft]);

  const refreshVersions = useCallback(async () => {
    setEntriesLoading(true);
    try {
      const res = await API.listCourseVersions(grpId, { limit: VERSIONS_PAGE });
      const data = res?.data?.data;
      if (unmountedRef.current || !data) return;
      setEntries((data.versions || []).map(toEntry));
      setLive((prev) =>
        prev ? { ...prev, version: data.liveVersion } : { version: data.liveVersion, publishedAt: null }
      );
    } catch {
      /* the drawer keeps what it has */
    } finally {
      if (!unmountedRef.current) setEntriesLoading(false);
    }
  }, [grpId]);

  const reloadDraft = useCallback(async () => {
    try {
      const data = await fetchDraft();
      if (unmountedRef.current || !data) return;
      applyDraft(data, true);
      setDraftLoaded(true);
      setDraftError('');
    } catch (err) {
      if (isVersioningOffError(err)) return;
      toast.error('Could not reload the draft. Refresh the page to continue.');
    }
  }, [applyDraft, fetchDraft]);

  useEffect(() => {
    if (!enabled || !grpId) return undefined;
    let cancelled = false;
    setDraftLoaded(false);
    setDraftError('');
    (async () => {
      try {
        const data = await fetchDraft();
        if (cancelled || !data) return;
        applyDraft(data, true);
        setDraftLoaded(true);
      } catch (err) {
        if (cancelled || isVersioningOffError(err)) return;
        setDraftError('Could not load the program draft.');
      }
    })();
    refreshVersions();
    return () => {
      cancelled = true;
    };
  }, [enabled, grpId, fetchDraft, applyDraft, refreshVersions]);

  // ── Autosave ───────────────────────────────────────────────────────────────

  const scheduleFlush = useCallback((delay: number) => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      saveTimerRef.current = null;
      flushRef.current();
    }, delay);
  }, []);

  const flush = useCallback(
    async (reason?: CourseDraftSaveReason): Promise<boolean> => {
      const pending = pendingRef.current;
      if (!pending) return true;
      if (savingRef.current) return false; // the running save re-flushes when it finishes
      savingRef.current = true;
      pendingRef.current = null;
      setSaveState('saving');
      try {
        const res = await API.saveCourseDraft(grpId, {
          levels: pending.payload,
          baseVersion: baseVersionRef.current,
          ...(reason ? { reason } : {})
        });
        const data = res?.data?.data;
        if (unmountedRef.current) return true;
        if (!pendingRef.current) lastSavedJsonRef.current = pending.json;
        if (data) {
          setDirty(data.dirty);
          setSavedAt(data.updatedAt);
          if (data.versionCreated) refreshVersions();
        }
        setLockHolder(null);
        setSaveState(pendingRef.current ? 'pending' : 'saved');
        if (needsIdAdoption(pending.modules)) {
          const freshData = await fetchDraft().catch(() => null);
          if (freshData && !unmountedRef.current) {
            const fresh = draftToModules(freshData.draft.levels);
            modulesUpdaterRef.current?.((prev) => adoptServerIds(prev, pending.modules, fresh));
            if (currentModulesRef.current) {
              const adopted = adoptServerIds(currentModulesRef.current, pending.modules, fresh);
              currentModulesRef.current = adopted;
              if (!pendingRef.current) lastSavedJsonRef.current = payloadJson(adopted);
            }
          }
        }
        return true;
      } catch (err: any) {
        if (unmountedRef.current) return false;
        if (isVersioningOffError(err)) return false;
        const { status, code, data } = errorCode(err);
        if (status === 409 && code === 'BASE_VERSION') {
          toast.info(
            `This program was published (v${data?.liveVersion ?? '?'}) while you were away. Your draft was reloaded.`
          );
          await reloadDraft();
          return false;
        }
        if (status === 409 && code === 'EDIT_LOCK') {
          setLockHolder(data?.lock || { userId: '', name: 'Another admin', stage: '', acquiredAt: 0, heartbeatAt: 0 });
          lockHeldRef.current = false;
          pendingRef.current = pendingRef.current || pending;
          setSaveState('error');
          return false;
        }
        if (status === 423) {
          // Frozen org: the axios interceptor already toasted; keep the edits for the retry.
          pendingRef.current = pendingRef.current || pending;
          setSaveState('error');
          return false;
        }
        pendingRef.current = pendingRef.current || pending;
        setSaveState('error');
        scheduleFlush(AUTOSAVE_RETRY_MS);
        return false;
      } finally {
        savingRef.current = false;
        if (pendingRef.current && unmountedRef.current) {
          // Edits reported while this save was in flight and the Designer is
          // gone: one last write, nothing to update on screen.
          const last = pendingRef.current;
          pendingRef.current = null;
          API.saveCourseDraft(grpId, {
            levels: last.payload,
            baseVersion: baseVersionRef.current,
            reason: 'hidden'
          }).catch(() => undefined);
        } else if (pendingRef.current && !saveTimerRef.current) {
          scheduleFlush(0);
        }
      }
    },
    [grpId, fetchDraft, refreshVersions, reloadDraft, scheduleFlush]
  );
  flushRef.current = flush;

  const reportDraft = useCallback(
    (modules: ModuleDraft[]) => {
      currentModulesRef.current = modules;
      setEditorModules(modules);
      const json = payloadJson(modules);
      if (hydrationPendingRef.current) {
        // First report after a (re)load: the editor now holds the draft. Baseline, not an edit.
        hydrationPendingRef.current = false;
        lastSavedJsonRef.current = json;
        return;
      }
      if (json === lastSavedJsonRef.current) {
        if (!pendingRef.current) return;
        // Edited back to the saved state: nothing to write.
        pendingRef.current = null;
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
        setSaveState('saved');
        return;
      }
      if (pendingRef.current?.json === json) return;
      pendingRef.current = { modules, payload: buildTemplateLevelsPayload(modules), json };
      setSaveState('pending');
      scheduleFlush(AUTOSAVE_DEBOUNCE_MS);
    },
    [scheduleFlush]
  );

  // ── Edit lock, hidden / close flushes ─────────────────────────────────────

  useEffect(() => {
    if (!enabled || !grpId) return undefined;
    let stopped = false;
    const tick = async (action: 'acquire' | 'heartbeat') => {
      try {
        const res = await API.programEditLock(grpId, { action, stage: 'designer' });
        if (stopped || !res) return;
        if (res.acquired) {
          lockHeldRef.current = true;
          setLockHolder(null);
        } else {
          lockHeldRef.current = false;
          // Only a lock we can prove is someone else's may lock the editor.
          setLockHolder(isOtherHolder(res.lock) ? res.lock : null);
        }
      } catch {
        // Fail open: a lock call failing never blocks the editor.
      }
    };
    tick('acquire');
    const interval = setInterval(() => tick(lockHeldRef.current ? 'heartbeat' : 'acquire'), LOCK_HEARTBEAT_MS);

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flushRef.current('hidden');
    };
    const onPageHide = () => {
      const pending = pendingRef.current;
      if (pending && !savingRef.current) {
        pendingRef.current = null;
        API.saveCourseDraftKeepalive(grpId, { levels: pending.payload, baseVersion: baseVersionRef.current });
      }
      if (lockHeldRef.current) {
        lockHeldRef.current = false;
        API.programEditLock(grpId, { action: 'release' });
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      stopped = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
      if (pendingRef.current) flushRef.current('hidden');
      if (lockHeldRef.current) {
        lockHeldRef.current = false;
        API.programEditLock(grpId, { action: 'release' });
      }
    };
  }, [enabled, grpId]);

  // ── Editor handlers ────────────────────────────────────────────────────────

  const registerRestoreHandler = useCallback((handler: ((snapshot: ModuleDraft[]) => void) | null) => {
    restoreHandlerRef.current = handler;
  }, []);
  const registerModulesUpdater = useCallback(
    (updater: ((fn: (prev: ModuleDraft[]) => ModuleDraft[]) => void) | null) => {
      modulesUpdaterRef.current = updater;
    },
    []
  );

  // ── Version history: preview / restore ─────────────────────────────────────

  const openPreview = useCallback(
    async (id: string) => {
      const entry = entries.find((e) => e.id === id);
      if (!entry) return;
      if (previewEntry?.id === id) return;
      setPreviewLoadingId(id);
      try {
        const res = await API.getCourseVersion(grpId, id);
        const version = res?.data?.data;
        if (unmountedRef.current || !version) return;
        setPreviewEntry({ ...entry, snapshot: draftToModules(version.snapshot?.levels || []) });
      } catch (err) {
        if (isVersioningOffError(err)) return;
        toast.error('Could not load that version.');
      } finally {
        if (!unmountedRef.current) setPreviewLoadingId(null);
      }
    },
    [entries, grpId, previewEntry?.id]
  );

  const resume = useCallback(() => {
    if (!previewEntry) return;
    setPreviewEntry(null);
    toast.info('Back to your current draft. Nothing was reloaded or rebuilt.');
  }, [previewEntry]);

  const restore = useCallback(async () => {
    if (!previewEntry || lockHolder) return;
    const entry = previewEntry;
    try {
      await flush();
      await API.restoreCourseVersion(grpId, entry.id);
      setPreviewEntry(null);
      await reloadDraft();
      refreshVersions();
      toast.success(
        `Restored the ${formatDateTime(entry.at)} version into your draft. Learners are unaffected until you publish.`
      );
    } catch (err: any) {
      if (isVersioningOffError(err)) return;
      const { status, code, data } = errorCode(err);
      if (status === 409 && code === 'EDIT_LOCK') {
        setLockHolder(data?.lock || null);
        toast.warn(
          `${data?.lock?.name || 'Another admin'} is editing this program, so restore is unavailable right now.`
        );
        return;
      }
      toast.error(err?.response?.data?.message || 'Could not restore that version.');
    }
  }, [previewEntry, lockHolder, flush, grpId, reloadDraft, refreshVersions]);

  // ── Publish ────────────────────────────────────────────────────────────────

  const openPublish = useCallback(async () => {
    await flush();
    setPublishOpen(true);
  }, [flush]);

  const noteLockHolder = useCallback(
    (lock: ProgramEditLock | null) => {
      if (!isOtherHolder(lock)) return;
      lockHeldRef.current = false;
      setLockHolder(lock);
    },
    [isOtherHolder]
  );

  const afterPublish = useCallback(
    async (versionNumber: number) => {
      await reloadDraft();
      await refreshVersions();
      setLive((prev) => (prev ? { ...prev, version: versionNumber } : { version: versionNumber, publishedAt: null }));
    },
    [reloadDraft, refreshVersions]
  );

  const lessonTitles = useMemo(() => {
    const map = new Map<string, string>();
    (editorModules || []).forEach((m) => m.lessons.forEach((l) => l.sourceId && map.set(l.sourceId, l.title)));
    return map;
  }, [editorModules]);

  const liveVersion = live?.version ?? 1;
  const hasChanges = dirty || saveState === 'pending' || saveState === 'saving' || saveState === 'error';

  const value = useMemo<CourseVersioningValue>(
    () => ({
      grpId,
      draftLoaded,
      draftError,
      draftModules,
      live,
      liveVersion,
      hasChanges,
      saveState,
      savedAt,
      lessonTags,
      lockHolder,
      readOnly: Boolean(lockHolder),
      noteLockHolder,
      entries,
      entriesLoading,
      panelOpen,
      setPanelOpen,
      previewEntry,
      previewLoadingId,
      openPreview,
      resume,
      restore,
      publishOpen,
      setPublishOpen,
      openPublish,
      afterPublish,
      lessonTitles,
      reportDraft,
      flushDraft: () => flush(),
      reloadDraft,
      fetchDraftModules,
      registerRestoreHandler,
      registerModulesUpdater
    }),
    [
      grpId,
      draftLoaded,
      draftError,
      draftModules,
      live,
      liveVersion,
      hasChanges,
      saveState,
      savedAt,
      lessonTags,
      lockHolder,
      noteLockHolder,
      entries,
      entriesLoading,
      panelOpen,
      previewEntry,
      previewLoadingId,
      openPreview,
      resume,
      restore,
      publishOpen,
      openPublish,
      afterPublish,
      lessonTitles,
      reportDraft,
      flush,
      reloadDraft,
      fetchDraftModules,
      registerRestoreHandler,
      registerModulesUpdater
    ]
  );

  return <CourseVersioningContext.Provider value={enabled ? value : null}>{children}</CourseVersioningContext.Provider>;
}
